// Copyright (c) 2025 Nebularis Holdings LLC
// Licensed to Nutrx Solutions under a non-exclusive, non-transferable license.
// See IP License Agreement for usage restrictions.
// frontend/src/components/enterprise-admin/roles/RoleDetailDrawer.jsx

import React, { useEffect, useMemo, useState } from "react";
import PermissionGroup from "./PermissionGroup";

function formatRoleKey(value) {
  return String(value || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function groupRolePermissions(role) {
  const groups = {};
  (role?.permissions || []).forEach((permission) => {
    const key = permission.permission_group || permission.group || "General";
    groups[key] = groups[key] || [];
    groups[key].push(permission);
  });
  return groups;
}

function assignedUsers(role) {
  if (Array.isArray(role?.assigned_users)) return role.assigned_users;
  if (Array.isArray(role?.users)) return role.users;
  return [];
}

function tabClass(activeTab, tab) {
  return activeTab === tab ? "active" : "";
}

function userStatus(value) {
  return String(value || "active")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

export default function RoleDetailDrawer({ role }) {
  const [activeTab, setActiveTab] = useState("overview");
  const grouped = useMemo(() => groupRolePermissions(role), [role]);
  const permissionCount = Array.isArray(role?.permissions)
    ? role.permissions.length
    : 0;
  const users = assignedUsers(role);
  const assignmentCount = Number(role?.assignment_count ?? users.length ?? 0);

  useEffect(() => {
    setActiveTab("overview");
  }, [role?.role_key]);

  if (!role) {
    return (
      <aside className="enterprise-admin-detail-panel enterprise-admin-role-detail">
        <p className="enterprise-admin-eyebrow">Role detail</p>
        <h3>Select a role</h3>
        <p className="enterprise-admin-muted">
          Review authority, assignment coverage, and included permissions.
        </p>
      </aside>
    );
  }

  return (
    <aside className="enterprise-admin-detail-panel enterprise-admin-role-detail">
      <div className="enterprise-admin-detail-heading">
        <p className="enterprise-admin-eyebrow">Role detail</p>
        <h3>{role.role_name || formatRoleKey(role.role_key)}</h3>
        <p className="enterprise-admin-muted">
          {role.description || "Organization authority role."}
        </p>

        <div className="enterprise-admin-role-detail-badges">
          <span>{role.is_system_role ? "System role" : "Custom role"}</span>
          <span>{role.is_assignable ? "Assignable" : "Not assignable"}</span>
          {role.is_default ? <span>Invitation default</span> : null}
          {role.is_deletable === false ? <span>Protected</span> : null}
        </div>
      </div>

      <div
        className="enterprise-admin-tabs"
        role="tablist"
        aria-label="Role detail sections"
      >
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === "overview"}
          className={tabClass(activeTab, "overview")}
          onClick={() => setActiveTab("overview")}
        >
          Overview
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === "permissions"}
          className={tabClass(activeTab, "permissions")}
          onClick={() => setActiveTab("permissions")}
        >
          Permissions <span>{permissionCount}</span>
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === "users"}
          className={tabClass(activeTab, "users")}
          onClick={() => setActiveTab("users")}
        >
          Members <span>{assignmentCount}</span>
        </button>
      </div>

      {activeTab === "overview" ? (
        <section className="enterprise-admin-tab-panel">
          <dl className="enterprise-admin-definition-list">
            <div>
              <dt>Assigned members</dt>
              <dd>{assignmentCount}</dd>
            </div>
            <div>
              <dt>Permissions</dt>
              <dd>{permissionCount}</dd>
            </div>
            <div>
              <dt>Role type</dt>
              <dd>{role.is_system_role ? "System role" : "Custom role"}</dd>
            </div>
            <div>
              <dt>Assignment</dt>
              <dd>{role.is_assignable ? "Available" : "Restricted"}</dd>
            </div>
          </dl>

          <div className="enterprise-admin-role-governance-note">
            <strong>Permissions enforced by OASYS</strong>
            <p>
              Active role assignments are combined with membership authority
              by the backend. Access checks remain organization-scoped and every
              administrative change is recorded in audit history.
            </p>
          </div>

          <details className="enterprise-admin-role-technical-details">
            <summary>Technical role details</summary>
            <dl>
              <div>
                <dt>Role key</dt>
                <dd><code>{role.role_key}</code></dd>
              </div>
              <div>
                <dt>Deletion protection</dt>
                <dd>{role.is_deletable === false ? "Protected" : "Not protected"}</dd>
              </div>
            </dl>
          </details>
        </section>
      ) : null}

      {activeTab === "permissions" ? (
        <section className="enterprise-admin-tab-panel">
          <div className="enterprise-admin-card-heading compact">
            <p className="enterprise-admin-eyebrow">Included permissions</p>
            <h4>What this role can access</h4>
          </div>

          {Object.keys(grouped).length ? (
            <div className="enterprise-admin-permission-stack enterprise-admin-role-permission-stack compact-list">
              {Object.entries(grouped).map(([group, rolePermissions]) => (
                <PermissionGroup
                  key={group}
                  title={group}
                  permissions={rolePermissions}
                  compact
                  collapsible
                  defaultOpen={Object.keys(grouped).length <= 4}
                />
              ))}
            </div>
          ) : (
            <div className="enterprise-admin-empty compact">
              <strong>No permissions included</strong>
              <p>This role currently provides no runtime access.</p>
            </div>
          )}
        </section>
      ) : null}

      {activeTab === "users" ? (
        <section className="enterprise-admin-tab-panel">
          <div className="enterprise-admin-card-heading compact">
            <p className="enterprise-admin-eyebrow">Assignments</p>
            <h4>Members with this role</h4>
          </div>

          {users.length ? (
            <div className="enterprise-admin-card-stack compact">
              {users.map((user) => (
                <article
                  key={user.user_id || user.id || user.email}
                  className="enterprise-admin-assigned-role-card enterprise-admin-role-user-card"
                >
                  <div>
                    <strong>
                      {user.name || user.email || user.user_id || user.id}
                    </strong>
                    <span>{user.email || user.user_id || user.id}</span>
                  </div>
                  <span
                    className={`enterprise-admin-status enterprise-admin-status-${String(
                      user.status || "active"
                    )
                      .toLowerCase()
                      .replace(/_/g, "-")}`}
                  >
                    {userStatus(user.status)}
                  </span>
                </article>
              ))}
            </div>
          ) : (
            <div className="enterprise-admin-empty compact">
              <strong>No members assigned</strong>
              <p>This role is available but is not assigned to an active member.</p>
            </div>
          )}
        </section>
      ) : null}
    </aside>
  );
}
